/**
 * OpenAI API integration for Abhi AI
 */

// System prompt defining the AI's behavior
const systemPrompt = `
You are Abhi AI, an advanced cybersecurity intelligence assistant developed by Abhi Pvt Ltd for fintech and banking security operations.

Your capabilities include:
- Providing the latest information on cybersecurity threats and vulnerabilities specifically targeting fintech and banking sectors up to 2025
- Analyzing and decoding various types of cryptographic hashes and security-related data
- Examining malicious code and explaining file behaviors in financial system contexts
- Offering cybersecurity best practices and recommendations for fintech and banking environments
- Providing updates on emerging threats in the financial services cybersecurity landscape
- Tracking cybersecurity trends and threat actors specifically targeting financial institutions through 2025
- Analyzing security incidents and providing threat intelligence for banking operations
- Offering guidance on compliance requirements (PCI DSS, SOX, Basel III, etc.) for financial institutions
- Providing security assessments and recommendations for fintech applications and banking infrastructure

When asked who you were created by, explain that you were developed by Abhi Pvt Ltd for their Security Command Center.
When asked what you can do, explain your cybersecurity capabilities including finding latest threats targeting financial services, 
decoding hashes, analyzing malware targeting banks, and other cybersecurity-related functions specific to fintech and banking.

Focus on cybersecurity threats, vulnerabilities, and defensive measures specifically relevant to:
- Banking systems and infrastructure
- Payment processing platforms
- Financial data protection
- Fintech applications security
- Regulatory compliance in financial services
- Fraud detection and prevention
- Digital banking security
- Cryptocurrency and blockchain security

The current year is 2025, and you have knowledge of cybersecurity events, threats, and technologies up to this point.
`;

/**
 * Sends a message to the OpenAI API via our backend proxy
 * @param message The user's message
 * @returns Promise with the AI response text
 */
export async function sendMessage(message: string): Promise<string> {
  try {
    console.log("Sending message to API...");
    
    // Check if we're in production (deployed on Cloudflare)
    const isProduction = import.meta.env.PROD;
    
    // For Cloudflare Pages, we'll use a direct API call with environment variables from Cloudflare
    if (isProduction) {
      try {
        // Try to use Cloudflare Functions (if available)
        const response = await fetch('/api/openai', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ message }),
        });
        
        if (!response.ok) {
          throw new Error('Cloudflare Function failed');
        }
        
        const data = await response.json();
        return data.response;
      } catch (cloudflareError) {
        console.error("Cloudflare Function error:", cloudflareError);
        
        // Fallback to backend API if available
        try {
          const apiBaseUrl = import.meta.env.VITE_API_URL || 'https://api.abhipvtltd.com';
          const apiUrl = `${apiBaseUrl}/api/chat`;
          
          const backendResponse = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ message }),
          });
          
          if (!backendResponse.ok) {
            throw new Error('Backend API failed');
          }
          
          const backendData = await backendResponse.json();
          return backendData.response;
        } catch (backendError) {
          console.error("Backend API error:", backendError);
          throw new Error('All API methods failed');
        }
      }
    } else {
      // In development, use the local server
      const apiUrl = '/api/chat';
      
      // Check server status first
      let statusUrl = '/api/status';
      
      const statusCheck = await fetch(statusUrl)
        .then(res => res.ok)
        .catch(() => false);
        
      if (!statusCheck) {
        console.warn("Backend server appears to be offline");
        throw new Error("Backend server is not responding");
      }
      
      // Use our backend proxy
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // If the server returns an error but includes a fallback response
        if (data.fallbackResponse) {
          return data.fallbackResponse;
        }
        throw new Error(data.error || 'Failed to get response from API');
      }
      
      return data.response;
    }
  } catch (error) {
    console.error("Error calling OpenAI API:", error);
    
    // Fallback response in case of API errors
    return "I apologize, but I'm currently experiencing connectivity issues. As an alternative, here are some general cybersecurity recommendations for financial services: 1) Keep all systems and software updated with latest security patches, 2) Use strong, unique passwords with multi-factor authentication, 3) Implement network segmentation and monitoring, 4) Regularly backup critical financial data, 5) Conduct security awareness training for staff, and 6) Maintain compliance with financial regulations like PCI DSS.";
  }
}